/**
 * IframeManager - Creates and manages the payment iframe
 */

export interface IframeConfig {
  /** URL to load in the iframe */
  src: string;

  /** Container element to inject iframe into */
  container: HTMLElement;

  /** Custom CSS for the iframe */
  css?: string;

  /** Callback when iframe loads */
  onLoad?: () => void;
}

const DEFAULT_IFRAME_CSS = 'width: 100%; border: none; min-height: 280px;';

export class IframeManager {
  private iframe: HTMLIFrameElement | null = null;
  private container: HTMLElement | null = null;

  /**
   * Create and inject an iframe into the container
   */
  create(config: IframeConfig): HTMLIFrameElement {
    // Create iframe element
    const iframe = document.createElement('iframe');
    iframe.id = 'pce-iframe';
    iframe.src = config.src;
    iframe.style.cssText = config.css || DEFAULT_IFRAME_CSS;

    // Security attributes
    iframe.setAttribute('scrolling', 'no');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowtransparency', 'true');

    // Load handler
    if (config.onLoad) {
      iframe.addEventListener('load', config.onLoad, { once: true });
    }

    // Clear container and inject iframe
    config.container.innerHTML = '';
    config.container.appendChild(iframe);

    this.iframe = iframe;
    this.container = config.container;

    return iframe;
  }

  /**
   * Get the iframe's content window for postMessage
   */
  getContentWindow(): Window | null {
    return this.iframe?.contentWindow || null;
  }

  /**
   * Get the iframe element
   */
  getIframe(): HTMLIFrameElement | null {
    return this.iframe;
  }

  /**
   * Update iframe height (for auto-resize)
   */
  setHeight(height: number): void {
    if (this.iframe) {
      this.iframe.style.height = `${height}px`;
    }
  }

  /**
   * Destroy the iframe and clean up
   */
  destroy(): void {
    if (this.iframe && this.container) {
      this.container.removeChild(this.iframe);
    }
    this.iframe = null;
    this.container = null;
  }
}
